import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { Observable } from 'rxjs';
import { AppService } from 'src/app/core';
import {
  Breadcrumb,
  BreadcrumbService,
  HeaderData
} from 'src/app/core/services/breadcrumb.service';
import { LoggingService } from 'src/app/core/services/logging.service';
import { AccessManegerService } from 'src/app/views/access-manager/access-manager.service';
import { UserModel } from 'src/app/views/access-manager/interface';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnInit {
  @ViewChild('profileMenuRef') profileMenuRef!: ElementRef<HTMLElement>;
  @ViewChild('avatarBtnRef') avatarBtnRef!: ElementRef<HTMLElement>;

  profileData: UserModel = {
    userName: '',
    phoneNumber: '',
    isActive: false,
    adminRoles: []
  };

  headerData$?: Observable<HeaderData>;
  isEmbeddedView = false;
  isProfileOpen = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggingService);

  constructor(
    private readonly keycloakService: KeycloakService,
    private readonly profileService: AccessManegerService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly appService: AppService,
    private readonly router: Router
  ) {}

  get tenantName(): string {
    return environment.workspaceName ?? 'JioLoyalty';
  }

  get roleLabel(): string {
    if (this.profileData.adminRoles?.includes('jiox-admin')) {
      return 'Super Admin';
    }
    return this.profileData.adminRoles?.[0] ?? '';
  }

  ngOnInit(): void {
    this.headerData$ = this.breadcrumbService.headerData$;

    this.appService.isEmbeddedViewasObservable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((bool) => {
        this.isEmbeddedView = bool;
      });

    this.profileService
      .getProfileDetail()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.profileData = response;
      });
  }

  toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isProfileOpen = !this.isProfileOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    const menuEl = this.profileMenuRef?.nativeElement;
    const btnEl = this.avatarBtnRef?.nativeElement;
    if (
      this.isProfileOpen &&
      menuEl &&
      btnEl &&
      !menuEl.contains(target) &&
      !btnEl.contains(target)
    ) {
      this.isProfileOpen = false;
    }
  }

  logout(): void {
    this.isProfileOpen = false;
    // Invalidate the Keycloak session server-side first so the bearer token cannot be reused
    // after this browser is gone. If the server call fails we still complete the Keycloak
    // logout so the user is not stranded in a half-logged-out state.
    this.profileService.logout().subscribe({
      next: () => this.completeKeycloakLogout(),
      error: (error: unknown) => {
        this.logger.error('Server-side logout failed', error);
        this.completeKeycloakLogout();
      }
    });
  }

  private completeKeycloakLogout(): void {
    this.keycloakService
      .logout(window.location.origin)
      .catch((error: unknown) => this.logger.error('Logout failed', error));
  }

  navigate(url: string): void {
    this.router
      .navigateByUrl(url)
      .catch((e: unknown) => this.logger.error('Navigation failed', e));
  }

  getNumber(value: Breadcrumb[] | undefined): number {
    return value?.length ?? 0;
  }
}
